import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { integrationAccountKeys } from "@/lib/query";

export type LinkedAccount = {
  nymblAccountId: string;
  clientAccountId: string;
  name: string;
  isActive: boolean;
  lastSyncedAt: string;
};

export type AvailableAccount = {
  nymblAccountId: string;
  name: string;
  isActive: boolean;
  lastSyncedAt: string;
};

export type IntegrationAccountsResponse = {
  configured: boolean;
  linked: LinkedAccount[];
  available: AvailableAccount[];
};

export type RefreshAccountsResult = {
  fetched: number;
  linkedUpdated: number;
  linkedUnchanged: number;
  newDiscovered: number;
  errors: number;
};

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export function useIntegrationAccounts() {
  return useQuery({
    queryKey: integrationAccountKeys.list(),
    queryFn: () => jsonFetch<IntegrationAccountsResponse>("/api/integrations/accounts"),
  });
}

export function useRefreshAccounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      jsonFetch<RefreshAccountsResult>("/api/integrations/accounts/refresh", { method: "POST" }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: integrationAccountKeys.all });
      toast.success(
        `Refreshed — ${r.fetched} fetched, ${r.linkedUpdated} updated, ${r.newDiscovered} new` +
          (r.errors > 0 ? `, ${r.errors} errors` : ""),
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
